import { Autocomplete, Box, CircularProgress, TextField, Typography } from '@mui/material';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { CrudService } from '../../core/services/crud.service';
import type { ListQuery } from '../../core/types/api';

interface TreeEntitySelectProps<T extends { id: number; name?: string }> {
  cacheKey: readonly unknown[];
  service: CrudService<T, Partial<T>, Partial<T>>;
  query?: ListQuery;
  value: number | null | undefined;
  onChange: (id: number | null) => void;
  label: string;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  /**
   * Name of the field on T pointing to the parent id. For Brand it's
   * `'parentBrandId'`; for Category it's `'commonCategoryId'`; etc.
   * Must reference a `number | null` property.
   */
  parentIdField: keyof T;
  /**
   * Optional `(entity) => label` override. Defaults to `entity.name`.
   * The label is used both for display and for typed-filter matching, so
   * keep it plain (no indentation prefixes — those are added in renderOption).
   */
  getLabel?: (entity: T) => string;
  /**
   * Entity id to omit along with its entire subtree — used when picking
   * a parent to prevent cycles (a node can't be its own descendant).
   */
  excludeSubtreeOf?: number;
}

interface FlatNode<T> {
  entity: T;
  depth: number;
}

function buildTree<T extends { id: number }>(
  rows: T[],
  parentIdField: keyof T,
  getLabel: (t: T) => string,
  excludeRoot?: number,
): FlatNode<T>[] {
  const byParent = new Map<number | null, T[]>();
  for (const row of rows) {
    const parentId = (row[parentIdField] as number | null | undefined) ?? null;
    const bucket = byParent.get(parentId);
    if (bucket) bucket.push(row);
    else byParent.set(parentId, [row]);
  }
  for (const list of byParent.values()) {
    list.sort((a, b) => getLabel(a).localeCompare(getLabel(b)));
  }

  // Surface orphans (rows whose parent isn't in the loaded set) as roots
  // so they don't silently disappear when the loaded page is truncated.
  const knownIds = new Set(rows.map((r) => r.id));
  const orphanRoots = rows
    .filter((r) => {
      const pid = r[parentIdField] as number | null | undefined;
      return pid != null && !knownIds.has(pid);
    })
    .sort((a, b) => getLabel(a).localeCompare(getLabel(b)));

  const result: FlatNode<T>[] = [];
  const visit = (row: T, depth: number) => {
    if (row.id === excludeRoot) return; // also skips its whole subtree
    result.push({ entity: row, depth });
    for (const child of byParent.get(row.id) ?? []) visit(child, depth + 1);
  };
  for (const root of byParent.get(null) ?? []) visit(root, 0);
  for (const orphan of orphanRoots) visit(orphan, 0);
  return result;
}

/**
 * Autocomplete for picking one entity out of a self-referencing tree
 * (Brand → parentBrandId, Category → commonCategoryId, Topic → parentId,
 * …). Options are flattened in DFS order and indented by depth so the
 * hierarchy is visible while typing-to-filter still works on plain names.
 */
export function TreeEntitySelect<T extends { id: number; name?: string }>({
  cacheKey,
  service,
  query,
  value,
  onChange,
  label,
  helperText,
  required,
  disabled,
  parentIdField,
  getLabel,
  excludeSubtreeOf,
}: TreeEntitySelectProps<T>) {
  const list = useQuery({
    queryKey: cacheKey,
    queryFn: () => service.list({ pageSize: 200, sort: 'name', ...query }),
  });

  const labelFor = useMemo(
    () => getLabel ?? ((o: T) => o.name ?? `#${o.id}`),
    [getLabel],
  );

  const flat = useMemo(
    () => buildTree(list.data?.data ?? [], parentIdField, labelFor, excludeSubtreeOf),
    [list.data, parentIdField, labelFor, excludeSubtreeOf],
  );

  const selected = flat.find((f) => f.entity.id === value) ?? null;

  return (
    <Autocomplete<FlatNode<T>, false, false, false>
      fullWidth
      disabled={disabled}
      options={flat}
      value={selected}
      onChange={(_, next) => onChange(next?.entity.id ?? null)}
      getOptionLabel={(o) => labelFor(o.entity)}
      isOptionEqualToValue={(a, b) => a.entity.id === b.entity.id}
      loading={list.isLoading}
      renderOption={(props, option) => (
        <Box
          component="li"
          {...props}
          key={option.entity.id}
          sx={{ pl: 2 + option.depth * 2 }}
        >
          {option.depth > 0 && (
            <Typography component="span" sx={{ color: 'text.disabled', mr: 1 }}>
              {'└─'}
            </Typography>
          )}
          <Typography variant="body2">{labelFor(option.entity)}</Typography>
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {list.isLoading ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
